/**
 * Display-only company name formatting.
 *
 * Strips a trailing legal-entity suffix (LLC, Inc., Corp, Ltd, ...) so the UI
 * shows a clean brand-style name. The database always keeps the FULL legal
 * name — never use this helper for anything written back to the DB, for
 * legal/credit/compliance documents, or for admin screens.
 */

const ENTITY_SUFFIXES = [
  "llc",
  "l.l.c.",
  "l.l.c",
  "l.c.",
  "lc",
  "inc",
  "inc.",
  "incorporated",
  "corp",
  "corp.",
  "corporation",
  "co",
  "co.",
  "company",
  "ltd",
  "ltd.",
  "limited",
  "lp",
  "l.p.",
  "llp",
  "l.l.p.",
  "lllp",
  "pllc",
  "p.l.l.c.",
  "pc",
  "p.c.",
  "pa",
  "p.a.",
  "gmbh",
  "sa",
  "s.a.",
  "nv",
  "n.v.",
  "bv",
  "b.v.",
  "pty",
  "plc",
  "p.l.c.",
];

const SUFFIX_SET = new Set(ENTITY_SUFFIXES);

/** Placeholder names that must render exactly as stored. */
const PASSTHROUGH = new Set(["a shipper on lanerunner"]);

function stripOnce(value: string): string | null {
  // Split off the last whitespace-delimited token.
  const match = value.match(/^(.*?)[\s,]+([^\s,]+)$/);
  if (!match) return null;

  const head = match[1].replace(/[\s,]+$/, "");
  const tail = match[2].toLowerCase();

  if (!head) return null;
  if (!SUFFIX_SET.has(tail)) return null;
  // "Boris & Company LLC" -> "Boris & Company": never leave a dangling conjunction.
  if (/(?:[&+]|\band)$/i.test(head)) return null;

  return head;
}

export function displayCompanyName(name?: string | null): string {
  const original = (name ?? "").trim();
  if (!original) return "";
  if (PASSTHROUGH.has(original.toLowerCase())) return original;

  // Allow at most two passes so "Smith Trucking Co., Inc." -> "Smith Trucking".
  let result = original;
  for (let i = 0; i < 2; i++) {
    const stripped = stripOnce(result);
    if (!stripped) break;
    result = stripped;
  }

  return result || original;
}

export default displayCompanyName;
